import { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

/**
 * Inquiry form modal. Chrome and typography mirror the RSVP modal so
 * the two form experiences read as part of the same system. The submit
 * button matches the gallery's existing dark-navy "Add to Cart" button
 * (navy background, white text, gold-wash on hover) so the flow from
 * the Inquire CTA → Send Inquiry CTA feels visually continuous.
 */
export function InquireModal({ isOpen, onClose, pieceName, artistName }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [serverError, setServerError] = useState("");
  const firstInputRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  // Reset internal state whenever the modal opens; preserve form data on
  // errors so the visitor can retry without retyping.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    setStatus("idle");
    setErrors({});
    setServerError("");
    const t = setTimeout(() => firstInputRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, []);

  const update = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;
    setStatus("submitting");
    setServerError("");
    try {
      const { data } = await api.post("/inquiries", {
        piece_name: pieceName || "",
        artist_name: artistName || "",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      if (!data?.success) throw new Error("submission failed");
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
      autoCloseTimerRef.current = setTimeout(() => onClose?.(), 3000);
    } catch (err) {
      const is429 = err?.response?.status === 429;
      setServerError(
        is429
          ? "Too many requests. Please try again in a few minutes."
          : "Something went wrong. Please try again or email us directly at parkerjr2@gmail.com."
      );
      setStatus("idle");
    }
  };

  // Styling copied verbatim from RSVPModal for visual parity.
  const inputClass = "w-full border border-[#0A1128]/15 px-4 py-3 text-sm focus:border-[#0A1128] focus:ring-1 focus:ring-[#0A1128] outline-none transition-colors";
  const labelClass = "block text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2";

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] bg-[#0A1128]/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inquire-modal-title"
        data-testid="inquire-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-[520px] max-h-[90vh] overflow-y-auto relative"
          data-testid="inquire-modal"
        >
          <button
            onClick={onClose}
            aria-label="Close inquiry form"
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-[#8A94A6] hover:text-[#0A1128] transition-colors"
            data-testid="inquire-modal-close"
          >
            <X size={18} />
          </button>

          <div className="px-8 py-10 md:px-10 md:py-12">
            {status === "success" ? (
              <div className="text-center py-8" data-testid="inquire-modal-success">
                <div className="w-12 h-[1px] bg-[#CBA365] mx-auto mb-6" />
                <h2
                  className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-4"
                  style={{ letterSpacing: "-0.01em" }}
                >
                  Thank you!
                </h2>
                <p className="text-sm text-[#4A5568] leading-relaxed mb-8">
                  We'll be in touch soon.
                </p>
                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-2 bg-[#0a1929] text-white transition-all duration-300 hover:bg-[#CBA365] hover:text-[#0A1128]"
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    padding: "1.1rem 2.5rem",
                  }}
                  data-testid="inquire-close-button"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <p className="overline mb-3">Inquiring About</p>
                  <h2
                    id="inquire-modal-title"
                    className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-2"
                    style={{ letterSpacing: "-0.01em" }}
                    data-testid="inquire-modal-piece-name"
                  >
                    {pieceName || "Untitled"}
                  </h2>
                  {artistName && (
                    <p
                      className="font-heading italic"
                      style={{
                        fontSize: "1rem",
                        color: "rgba(10, 17, 40, 0.7)",
                        textTransform: "none",
                        WebkitTextStroke: "0",
                        textShadow: "none",
                        letterSpacing: "0",
                      }}
                      data-testid="inquire-modal-artist-name"
                    >
                      by {artistName}
                    </p>
                  )}
                </div>

                {serverError && (
                  <div
                    className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm"
                    role="alert"
                    aria-live="polite"
                    data-testid="inquire-modal-error"
                  >
                    {serverError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div>
                    <label htmlFor="inquire-name" className={labelClass}>Name *</label>
                    <input
                      id="inquire-name"
                      ref={firstInputRef}
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className={inputClass}
                      disabled={status === "submitting"}
                      data-testid="inquire-input-name"
                      autoComplete="name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="inquire-email" className={labelClass}>Email *</label>
                    <input
                      id="inquire-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputClass}
                      disabled={status === "submitting"}
                      data-testid="inquire-input-email"
                      autoComplete="email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="inquire-phone" className={labelClass}>Phone</label>
                    <input
                      id="inquire-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputClass}
                      disabled={status === "submitting"}
                      data-testid="inquire-input-phone"
                      autoComplete="tel"
                    />
                  </div>

                  <div>
                    <label htmlFor="inquire-message" className={labelClass}>How can we help you?</label>
                    <textarea
                      id="inquire-message"
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Questions about this piece, pricing, shipping, etc."
                      className={`${inputClass} resize-none`}
                      disabled={status === "submitting"}
                      data-testid="inquire-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inquire-sweep relative overflow-hidden w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: "#0a1929",
                      color: "#ffffff",
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      padding: "1.1rem 2rem",
                      transition: "color 0.4s ease",
                    }}
                    data-testid="inquire-modal-submit"
                  >
                    <span
                      aria-hidden="true"
                      className="inquire-sweep__fill absolute inset-0"
                      style={{ backgroundColor: "#CBA365" }}
                    />
                    <span className="relative z-10 flex items-center gap-2">
                      {status === "submitting" ? (
                        <><Loader2 size={14} className="animate-spin" /> Sending...</>
                      ) : (
                        "Send Inquiry"
                      )}
                    </span>
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
